import axios from 'axios';
import config from '../config';

/**
 * Get ImageKit auth parameters from backend
 * Backend generates using SDK's getAuthenticationParameters()
 */
const getImageKitAuthToken = async () => {
  try {
    console.log('[ImageKit] Requesting auth from backend...');
    const response = await axios.post(`${config}/candidate/imagekit-token`);
    
    const { signature, expire, token, publicKey } = response.data;
    
    if (!signature || !expire || !token || !publicKey) {
      throw new Error('Missing auth parameters: ' + 
        JSON.stringify({ signature: !!signature, expire: !!expire, token: !!token, publicKey: !!publicKey })
      );
    }
    
    console.log('[ImageKit] Auth received');
    return response.data;
  } catch (error) {
    console.error('[ImageKit] Failed to get auth:', error.message);
    throw new Error(`Failed to get ImageKit auth: ${error.message}`);
  }
};

/**
 * Upload file to ImageKit
 * @param {File} file - The file to upload
 * @param {string} folder - ImageKit folder path
 * @returns {Promise<string>} ImageKit URL
 */
export const uploadToImageKit = async (file, folder = 'candidate') => {
  if (!file) throw new Error('File is required');

  try {
    console.log('[ImageKit] Uploading:', file.name);
    const auth = await getImageKitAuthToken();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', `${Date.now()}-${file.name}`);
    formData.append('folder', folder);
    formData.append('publicKey', auth.publicKey);
    formData.append('signature', auth.signature);
    formData.append('expire', auth.expire);
    formData.append('token', auth.token);

    console.log('[ImageKit] Sending to ImageKit upload API...');
    const response = await axios.post(
      'https://upload.imagekit.io/api/v1/files/upload',
      formData,
      { timeout: 30000 }
    );

    if (!response.data.url) throw new Error('No URL in response');
    
    console.log('[ImageKit] Upload successful:', response.data.url);
    return response.data.url;
  } catch (error) {
    console.error('[ImageKit] Upload error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Upload multiple files to ImageKit
 * @param {File[]} files - Array of files to upload
 * @param {string} folder - ImageKit folder path
 * @returns {Promise<string[]>} Array of ImageKit URLs
 */
export const uploadMultipleToImageKit = async (files, folder = 'candidate') => {
  if (!files || !Array.isArray(files) || files.length === 0) {
    throw new Error('Files array is required and must not be empty');
  }

  console.log(`[ImageKit] Starting batch upload of ${files.length} files...`);
  
  try {
    const uploadPromises = files.map((file, index) => {
      console.log(`[ImageKit] Queuing file ${index + 1}/${files.length}: ${file.name}`);
      return uploadToImageKit(file, folder);
    });
    
    const results = await Promise.all(uploadPromises);
    console.log(`[ImageKit] Batch upload complete. All ${results.length} files uploaded.`);
    return results;
  } catch (error) {
    console.error('[ImageKit] Batch upload failed:', error.message);
    throw new Error(`Batch upload failed: ${error.message}`);
  }
};
